import { getHighlightPositions, getTableCitationContent, HighlightPositionsData } from '@mygpt/app/chat/services';
import { create } from 'zustand';
import toast from 'react-hot-toast';
import i18next from 'i18next';

type Store = {
  // 后端传递的参数
  file_id: string;
  // 后端参数，引用页码，从0开始计数
  page: number;
  // ------ tableRAG 引用特有数据 ------
  partialExcelContent: any[][]; // tableRAG引用特有，表示被引用的Excel部分内容片段，用来展示在预览面板中
  sheet_name: string; // tableRAG引用特有，预览时表示当前展示的sheet名称
  rows: number[]; // tableRAG引用特有，表示被引用的行
  sheet: string; // tableRAG引用特有，表示被引用的表格sheet index
  // ---------------------------------
  // 当前引用对应的答案片段
  content: string;
  title?: string;
  highlightPositions: HighlightPositionsData['positions'];
  // 答案中返回的page信息可能不准确，高亮接口返回的page相对准确
  // 因此把高亮接口返回的page保存起来，方便用户第二次鼠标悬浮答案中的引用标签时，直接在tooltip中展示更准确的页码。
  // 存在前端内存中
  // {
  //   [file_id+content]: page
  // }
  pageMap: Record<string, number>;
  setReferenceInfo: ({
    file_id,
    page,
    content,
    citationOrder,
    title,
  }: {
    file_id: string;
    page: number;
    content: string;
    citationOrder: number; // 引用的顺序，因为相同的内容后面可能出现多个引用，需要通过这个来区分
    title?: string;
  }) => void;
  setExcelReferenceInfo: ({
    file_id,
    rows,
    sheet,
    title,
  }: {
    file_id: string;
    rows: number[];
    sheet: string;
    title?: string;
  }) => void;
  clearReferenceInfo: () => void;
};

// 每次预览前都需要先重置的数据
// 防止前面的数据影响到了后续的预览
const NEED_RESET_DATA = {
  file_id: '',
  page: 0,
  content: '',
  title: '',
  highlightPositions: [],
  rows: [],
  sheet: '',
  partialExcelContent: [],
  sheet_name: '',
};

export const useFilePreviewStore = create<Store>()((set, get) => ({
  ...NEED_RESET_DATA,
  pageMap: {},
  setExcelReferenceInfo: ({ file_id, rows, sheet, title }) => {
    // 如果是 TableRAG 的引用
    if (rows && rows.length) {
      getTableCitationContent({ file_id, rows, sheet }).then((res) => {
        if (!res.data) return;
        const { data, sheet_name } = res.data;
        set(() => ({ partialExcelContent: data, sheet_name }));
      });

      // 其他excel无关的数据，还原成默认值
      set(() => ({ ...NEED_RESET_DATA, file_id, rows, sheet, title }));
      return;
    }
  },
  setReferenceInfo: ({ file_id, page, content, citationOrder, title }) => {
    const promise = getHighlightPositions({ file_id, page, text: content }).then((res) => {
      if (!res.data) return;

      const { positions } = res.data;

      if (positions && !positions.length) {
        set(() => ({
          highlightPositions: [],
          pageMap: Object.assign(get().pageMap, {
            [`${citationOrder}${file_id}${content}`]: -1, // -1 表示接口返回了，但没有高亮位置数据。底部回答的引用标签那里可以根据这个值做特殊处理。
          }),
        }));

        return Promise.reject();
      }

      const { pageMap } = get();
      const firstPage = positions[0].page; // 如果第一个高亮页面有值，那将页面滚动到该页面

      set(() => ({
        highlightPositions: positions,
        page: firstPage,
        pageMap: Object.assign(pageMap, {
          [`${citationOrder}${file_id}${content}`]: firstPage,
        }),
      }));

      return positions;
    });

    toast.promise(
      promise,
      {
        loading: i18next.t('preview_panel.get_highlight_position_loading'),
        success: i18next.t('preview_panel.get_highlight_position_success'),
        error: i18next.t('preview_panel.get_highlight_position_error'),
      },
      {
        toasterId: 'FILE_PREVIEW',
      }
    );

    // 其他 pdf 无关的数据，还原成默认值
    set(() => ({ ...NEED_RESET_DATA, file_id, page, content, title }));
  },
  clearReferenceInfo: () => set(() => ({ file_id: '', page: 0, content: '', highlightPositions: [], title: '' })),
}));
